/**
 * client/src/app/guards.tsx
 * PageLoader, NotFound, and Guarded route-guard components.
 * Extracted from App.tsx (300-line modularity rule).
 */

import { Redirect, useLocation, Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { AUTH_ME_QUERY_KEY } from "@/lib/auth-me";
import { LoadingState } from "@/components/ui/loading-state";
import { AalGatePanel } from "./aal-gate-panel";

// ---------------------------------------------------------------------------
// CurrentUser shape (mirrored here for guard prop types)
// ---------------------------------------------------------------------------

interface GuardedUser {
  aal: "AAL1" | "AAL2" | "AAL3";
  /** Shell context only (sidebar/header) for the below-floor gate panel
   *  (#1588) — never an authorization input. Optional so existing test
   *  fixtures that only supply `{ aal }` keep compiling. */
  displayName?: string;
}

// ---------------------------------------------------------------------------
// PageLoader
// ---------------------------------------------------------------------------

/**
 * Full-screen loading indicator shown during Suspense or auth fetch. Neutral
 * shared LoadingState breathing pulse (CLAUDE.md Design Constraints:
 * loading = neutral grey) — not a spin, and not emerald
 * (reserved for success/AAL milestones). Reduced motion is honored
 * app-globally via <MotionConfig reducedMotion="user"> in App.tsx (DR-23,
 * #1586), not a local check here.
 */
export function PageLoader({ label = "Getting things ready…" }: { label?: string } = {}) {
  return (
    <div className="min-h-dvh bg-bg-primary flex items-center justify-center">
      <LoadingState label={label} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// NotFound
// ---------------------------------------------------------------------------

/** 404 page — uses Wouter Link to stay within the SPA (fixes UI-086). */
export function NotFound() {
  return (
    <div className="min-h-dvh bg-bg-primary flex items-center justify-center px-4">
      <div className="text-center space-y-3">
        <p className="text-4xl font-bold text-text-tertiary">404</p>
        <p className="text-text-secondary text-sm">Page not found</p>
        <Link href="/auth" className="text-xs text-interactive underline underline-offset-2">
          Back to sign in
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Guarded
// ---------------------------------------------------------------------------

interface GuardedProps {
  user:     GuardedUser | null;
  loading:  boolean;
  minAal?:  "AAL1" | "AAL2" | "AAL3";
  children: React.ReactNode;
}

/**
 * Route guard that redirects unauthenticated users to /auth. An authenticated
 * user below the route's `minAal` floor never sees `children` (Friction Rule
 * clause 3 — the floor is real enforcement, unchanged) but, since #1588, no
 * longer gets silently bounced to the full /setup onboarding wizard either —
 * they see an in-shell `AalGatePanel` explaining why and offering the actual
 * next step (a real WebAuthn step-up, or the specific missing ceremony).
 */
export function Guarded({ user, loading, minAal = "AAL1", children }: GuardedProps) {
  const [,] = useLocation();
  const queryClient = useQueryClient();

  if (loading) return <PageLoader label="Checking your sign-in…" />;
  if (!user) {
    // #1693: tolerate a one-tick-stale `user` prop. A ceremony page (auth-entry)
    // writes fresh server truth into ["auth/me"] and navigates here in the SAME
    // microtask — deliberately before react-query's deferred observer flush
    // re-renders the shell with the new user (#1685). Redirecting on the stale
    // null prop chained /auth → /dashboard → /security-centre and dropped
    // brand-new accounts out of onboarding. A live cache that already holds a
    // user means the props are catching up: hold the frame; the shell re-renders
    // with the real user on the flush tick. An empty/null cache is a genuinely
    // signed-out visitor — redirect exactly as before (the floor is unchanged;
    // children are never rendered from this branch).
    if (queryClient.getQueryData(AUTH_ME_QUERY_KEY)) return <PageLoader label="Checking your sign-in…" />;
    return <Redirect to="/auth" />;
  }

  const aalRank: Record<string, number> = { AAL1: 1, AAL2: 2, AAL3: 3 };
  // Fail closed: an unknown/missing `user.aal` ranks as 0, so it gates rather
  // than falling through to `children` (undefined < N is false in JS).
  if ((aalRank[user.aal] ?? 0) < aalRank[minAal]) {
    // aalRank["AAL1"] is the minimum possible rank, so this branch is only
    // reachable when minAal is "AAL2" or "AAL3" — narrow without an `as` cast
    // (Type Patterns rule 5) by mapping the impossible "AAL1" case onto the
    // weaker of the two real floors rather than casting past the type.
    const elevatedMinAal: "AAL2" | "AAL3" = minAal === "AAL3" ? "AAL3" : "AAL2";
    return <AalGatePanel minAal={elevatedMinAal} currentAal={user.aal} displayName={user.displayName} />;
  }

  return <>{children}</>;
}
